import { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import PhoneInput from 'react-phone-input-2'
import ConfirmationCode from 'components/ConfirmationCode/ConfirmationCode'
import Loader from 'components/Base/Loader'
import BaseModal from 'components/Base/BaseModal'
import GoogleSign from 'components/GoogleSign/GoogleSign'
import { useStore } from 'store'
import { countdown, parseError } from 'utils/helpers'
import Logo from './img/logo.svg'

const Auth = () => {
    const [isLoading, setIsLoading] = useState(false);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState('');
    const [userType, setUserType] = useState('customer');

    const [phoneCode, setPhoneCode] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isEmailCodeSent, setIsEmailCodeSent] = useState(false);

    const [modalState, setModalState] = useState("main");
    const [resetStep, setResetStep] = useState(0);
    const [resetCode, setResetCode] = useState('');
    const [error, setError] = useState({});

    const [activeMethod, setActiveMethod] = useState(null);

    const store = useStore();
    const navigate = useNavigate();

    useEffect(() => {
        document.body.classList.add("tailwind-body");
        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    const handleBack = useCallback(() => {
        if (activeMethod) {
            setActiveMethod(null);
            setError({});
            setIsCodeSent(false);
            setIsEmailCodeSent(false);
        } else {
            navigate(-1);
        }
    }, [navigate, activeMethod]);

    const validateDOB = () => {
        if (!dateOfBirth) {
            setError({ message: "Date of birth is required." });
            return false;
        }
        return true;
    };

    const validateEmail = () => {
        if (!email) {
            setError({ message: "Email address is required." });
            return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError({ message: "Please enter a valid email address." });
            return false;
        }
        return true;
    };

    const signInWithPassword = useCallback(() => {
        setIsLoading(true);
        setError({});

        axios
            .post(`/auth/signin/${userType}`, { email, password })
            .then(({ data }) => {
                store.auth.authorize(data.token, userType, data.backgroundCheckResult);
                navigate('/');
            })
            .catch((e) => {
                setError(parseError(e));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [email, password, userType, store.auth, navigate]);

    const startReset = useCallback(() => {
        setModalState("reset");
        setResetStep(0);
        setResetCode('');
        setPassword('');
    }, []);

    const resetPassword = useCallback(() => {
        setIsLoading(true);
        setError({});
        if (resetStep === 0) {
            axios.post(`/auth/reset/${userType}`, { email }).then(({ data }) => {
                store.notif.create({ title: 'Reset started', content: 'Email sent.', type: 'info', autoClose: 15000 });
                setResetStep(1);
            }).catch((e) => setError(parseError(e))).finally(() => setIsLoading(false));
        }
        if (resetStep === 1) { setResetStep(2); setIsLoading(false); }
        if (resetStep === 2) {
            axios.post(`/auth/reset/confirm`, { email, password, code: resetCode.join('') })
                .then(({ data }) => {
                    setResetStep(2);
                    setModalState("main");
                    store.notif.create({ title: 'Success', content: 'Password reset successfully!', type: 'success', autoClose: 10000 });
                })
                .catch((e) => {
                    setError(parseError(e));
                })
                .finally(() => setIsLoading(false));
        }
    }, [store.notif, email, password, userType, resetCode, resetStep]);

    const googleSignIn = useCallback(
        ({ access_token }) => {
            axios
                .post(`/auth/signin/${userType}/google`, { access_token })
                .then(({ data }) => {
                    store.auth.authorize(data.token, userType, data.backgroundCheckResult);
                    navigate('/');
                })
                .catch((e) => {
                    const message = e.response?.data?.error || 'is invalid';
                    setError({ type: 'Google Auth', message });
                });
        },
        [userType, store.auth, navigate]
    );

    const closeModal = useCallback(() => {
        setModalState("main");
        setPhoneNumber('');
        setPhoneCode('');
        setIsCodeSent(false);
        setTimeLeft(0);
    }, []);

    const numberSignIn = async () => {
        if (!phoneNumber) { setError({ message: "Phone number is required." }); return; }
        try {
            setError({})
            await axios.post(`/auth/signin/number/${userType}`, { resend: isCodeSent, number: phoneNumber });
            setTimeLeft(60); countdown(setTimeLeft); setIsCodeSent(true)
        } catch (error) { setError(parseError(error)); }
    };

    const confirmNumberSignIn = async () => {
        if (!phoneCode) { setError({ message: "Verification code is required." }); return; }
        axios.post(`/auth/signin/number/confirm/${userType}`, { code: phoneCode, number: phoneNumber })
            .then(({ data }) => store.auth.authorize(data.token, userType, data.backgroundCheckResult))
            .catch((e) => setError(parseError(e)));
    };

    const sendEmailCode = useCallback(async () => {
        if (!validateEmail() || !validateDOB()) return;
        setIsLoading(true); setError({});
        try {
            await axios.post(`/auth/signin/email/${userType}`, { email, dob: dateOfBirth, resend: isEmailCodeSent });
            setTimeLeft(60); countdown(setTimeLeft); setIsEmailCodeSent(true);
        } catch (error) { setError(parseError(error)); } finally { setIsLoading(false); }
    }, [email, dateOfBirth, userType, isEmailCodeSent]);

    const confirmEmailCode = useCallback(async () => {
        if (!emailCode) { setError({ message: "Verification code is required." }); return; }
        setIsLoading(true); setError({});
        try {
            const { data } = await axios.post(`/auth/signin/email/confirm/${userType}`, { code: emailCode, email, dateOfBirth });
            store.auth.authorize(data.token, userType, data.backgroundCheckResult);
            navigate('/');
        } catch (error) { setError(parseError(error)); } finally { setIsLoading(false); }
    }, [emailCode, email, dateOfBirth, userType, store.auth, navigate]);

    const methodConfig = {
        google: {
            title: 'Continue with Google',
            icon: <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="currentColor" d="M21.35,11.1H12.18V13.83H18.69C18.36,17.64 15.19,19.27 12.19,19.27C8.36,19.27 5,16.25 5,12C5,7.9 8.2,4.73 12.2,4.73C15.29,4.73 17.1,6.7 17.1,6.7L19,4.72C19,4.72 16.56,2 12.1,2C6.42,2 2.03,6.8 2.03,12C2.03,17.05 6.16,22 12.25,22C17.6,22 21.5,18.33 21.5,12.91C21.5,11.76 21.35,11.1 21.35,11.1Z" /></svg>,
            description: 'Quick and secure sign in with your Google account'
        },
        emailCode: {
            title: 'Email + Date of Birth',
            icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
            description: 'Verify your identity with email and date of birth'
        },
        phone: {
            title: 'Phone Number',
            icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>,
            description: 'Use your phone number for quick verification'
        },
        password: {
            title: 'Email + Password',
            icon: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
            description: 'Sign in with your email and password'
        }
    };

    const commonButtonClasses = "w-full flex justify-center items-center py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200";
    const primaryButtonClasses = "bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white shadow-lg shadow-blue-500/20";
    const secondaryButtonClasses = "bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100";

    const renderMethodCard = (methodKey) => {
        const config = methodConfig[methodKey];
        return (
            <button
                key={methodKey}
                onClick={() => setActiveMethod(methodKey)}
                className="w-full group bg-white p-6 flex items-center justify-between text-left rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 border-2 border-transparent hover:border-sky-100 transition-all duration-300 ease-in-out"
            >
                <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-lg bg-gray-100 text-gray-600 group-hover:bg-sky-100 group-hover:text-sky-600 transition-colors duration-200">
                        {config.icon}
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{config.title}</h3>
                        <p className="text-sm text-gray-500">{config.description}</p>
                    </div>
                </div>
                <svg className="w-5 h-5 text-gray-300 group-hover:text-sky-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
            </button>
        );
    };

    const renderActiveForm = () => {
        switch (activeMethod) {
            case 'google':
                return (
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 animate-fadeIn">
                        <div className="text-center mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Sign in with Google</h3>
                            <p className="text-gray-500 mt-1">Pop-up window will open</p>
                        </div>
                        <GoogleSign onSign={googleSignIn} type="in" />
                    </div>
                );

            case 'emailCode':
                return (
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 animate-fadeIn space-y-5">
                        <div className="text-center mb-2">
                            <h3 className="text-xl font-bold text-gray-900">Sign in with Email</h3>
                            <p className="text-sm text-gray-500">Confirm your Date of Birth</p>
                        </div>

                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Date of Birth</label>
                            <input
                                type="date"
                                value={dateOfBirth}
                                onChange={(e) => setDateOfBirth(e.target.value)}
                                className="w-full p-3.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com"
                                className="w-full p-3.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>

                        {!isEmailCodeSent ? (
                            <button onClick={sendEmailCode} className={`${commonButtonClasses} ${primaryButtonClasses}`}>
                                Get Verification Code
                            </button>
                        ) : (
                            <div className="space-y-4 pt-2 border-t border-gray-100">
                                <p className="text-sm text-center text-gray-600 font-medium">Enter code sent to email</p>
                                <ConfirmationCode setConfirm={setEmailCode} />
                                {timeLeft > 0 ? (
                                    <p className="text-sm text-center text-gray-500">Resend in {timeLeft}s</p>
                                ) : (
                                    <button onClick={sendEmailCode} className={`${commonButtonClasses} ${secondaryButtonClasses}`}>
                                        Resend Code
                                    </button>
                                )}
                                <button
                                    onClick={confirmEmailCode}
                                    disabled={!emailCode}
                                    className={`${commonButtonClasses} ${primaryButtonClasses} disabled:opacity-50`}
                                >
                                    Verify & Sign In
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'phone':
                return (
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 animate-fadeIn space-y-5">
                        <div className="text-center mb-2">
                            <h3 className="text-xl font-bold text-gray-900">Sign in with Phone</h3>
                            <p className="text-sm text-gray-500">We'll send you an SMS code</p>
                        </div>
                        <div className="relative space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Phone Number</label>
                            <PhoneInput
                                containerClass="!w-full"
                                inputClass="!w-full !p-3.5 !pl-12 !h-auto !text-sm !bg-gray-50 !border-gray-200 !rounded-xl !focus:ring-2 !focus:ring-sky-500 !focus:border-transparent !transition-all"
                                buttonClass="!p-1 !rounded-l-xl !border-gray-200 !bg-gray-50"
                                country={'us'}
                                specialLabel=""
                                value={phoneNumber.toString()}
                                onChange={(phone) => setPhoneNumber(phone)}
                            />
                        </div>
                        {!isCodeSent ? (
                            <button onClick={numberSignIn} className={`${commonButtonClasses} ${primaryButtonClasses}`}>
                                Get Verification Code
                            </button>
                        ) : (
                            <div className="space-y-4 pt-2 border-t border-gray-100">
                                <p className="text-sm text-center text-gray-600 font-medium">Enter code from SMS</p>
                                <ConfirmationCode setConfirm={setPhoneCode} />
                                {timeLeft > 0 ? (
                                    <p className="text-sm text-center text-gray-500">Resend in {timeLeft}s</p>
                                ) : (
                                    <button onClick={numberSignIn} className={`${commonButtonClasses} ${secondaryButtonClasses}`}>
                                        Resend Code
                                    </button>
                                )}
                                <button
                                    onClick={confirmNumberSignIn}
                                    disabled={!phoneCode}
                                    className={`${commonButtonClasses} ${primaryButtonClasses} disabled:opacity-50`}
                                >
                                    Verify & Sign In
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'password':
                return (
                    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 animate-fadeIn space-y-5">
                        <div className="text-center mb-2">
                            <h3 className="text-xl font-bold text-gray-900">Password Login</h3>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Email Address</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Email address"
                                className="w-full p-3.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Password</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password"
                                className="w-full p-3.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>
                        <div className="pt-2">
                            <button onClick={signInWithPassword} className={`${commonButtonClasses} ${primaryButtonClasses}`}>
                                {isLoading ? (
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : 'Sign In'}
                            </button>
                            <button onClick={startReset} className="w-full mt-4 text-sm text-sky-600 hover:text-sky-700 font-medium">
                                Forgot password?
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-sky-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
            {/* Back Button */}
            <button
                type="button"
                onClick={handleBack}
                className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 text-sm font-medium text-gray-600 shadow ring-1 ring-gray-200 transition hover:text-blue-600 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 z-10"
            >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
                <span>{activeMethod ? 'All Methods' : 'Back'}</span>
            </button>

            <div className="sm:mx-auto sm:w-full sm:max-w-xl">
                {/* Logo Section */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block group">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-sky-400 to-blue-500 rounded-lg opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                            <img src={Logo} alt="Emissary Care" className="relative h-12 w-auto" />
                        </div>
                    </Link>
                </div>

                {/* Typography changes slightly based on view */}
                <div className="text-center space-y-4 mb-8">
                    <div className="space-y-2">
                        <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                            {activeMethod ? methodConfig[activeMethod].title : "Welcome Back"}
                        </h1>
                        <div className="w-16 h-1 bg-gradient-to-r from-blue-600 to-sky-600 rounded-full mx-auto"></div>
                    </div>
                    {!activeMethod && (
                        <p className="text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
                            Choose your preferred way to sign in to your account
                        </p>
                    )}
                </div>
            </div>

            {/* Error Message Display */}
            {error.message && (
                <div className="sm:mx-auto sm:w-full sm:max-w-xl mb-6 animate-pulse">
                    <div className="rounded-xl bg-red-50 border border-red-100 p-4 text-sm text-red-700 shadow-sm">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <span className="font-semibold">{error.type && `${error.type}: `}</span> {error.message}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MAIN CONTENT SWITCHER */}
            <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-xl transition-all duration-300">
                {!activeMethod ? (
                    <div className="space-y-4 animate-fadeIn">
                        {renderMethodCard('google')}
                        {renderMethodCard('emailCode')}
                        {renderMethodCard('phone')}
                        {renderMethodCard('password')}
                    </div>
                ) : (
                    renderActiveForm()
                )}
            </div>

            {/* Password Reset Modal (Same as before) */}
            {modalState === "reset" && (
                <BaseModal show={true} onClose={closeModal}>
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                        {/* ... Modal content kept exactly as original ... */}
                        <div className="sm:flex sm:items-start">
                            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-sky-100 sm:mx-0 sm:h-10 sm:w-10">
                                <svg className="h-6 w-6 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                <h3 className="text-lg leading-6 font-medium text-gray-900">Reset Password</h3>
                                <div className="mt-2">
                                    <p className="text-sm text-gray-500">
                                        {resetStep === 0 && "Enter your email to receive a reset code"}
                                        {resetStep === 1 && "Enter the verification code sent to your email"}
                                        {resetStep === 2 && "Create your new password"}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-6 space-y-4">
                            {resetStep === 0 && (
                                <div className="space-y-1.5">
                                    <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">Email address</label>
                                    <input id="resetEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-primary w-full p-3 border border-gray-300 rounded-lg" placeholder="you@example.com" />
                                </div>
                            )}
                            {resetStep === 1 && (<div className="space-y-4"><ConfirmationCode setConfirm={setResetCode} /></div>)}
                            {resetStep === 2 && (
                                <div className="space-y-1.5">
                                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">New password</label>
                                    <input id="newPassword" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-primary w-full p-3 border border-gray-300 rounded-lg" placeholder="••••••••" />
                                </div>
                            )}
                            <div className="mt-6 space-y-2">
                                <button onClick={resetPassword} className="w-full inline-flex justify-center py-3 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-sky-600 hover:bg-sky-700">{['Send Reset Code', 'Verify Code', 'Reset Password'][resetStep]}</button>
                                <button onClick={() => setModalState("main")} className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50">Back to Login</button>
                            </div>
                        </div>
                    </div>
                </BaseModal>
            )}

            <Loader show={isLoading} />
        </div>
    );
};

export default observer(Auth);